import { useState } from 'react';
import { X, UserPlus, Loader2 } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from 'axios';
import { toast } from 'sonner';

const GREEN = "linear-gradient(120deg,#15803D,#0F766E)";

const AdminAddUserModal = ({ isOpen, onClose, onAdded }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    business_name: '',
    abn: '',
    gst_registered: false,
    account_status: 'active',
    approval_status: 'approved',
    skills: []
  });
  const [creating, setCreating] = useState(false);

  if (!isOpen) return null;

  const roleOptions = [
    { value: 'photographer', label: 'Photographer' },
    { value: 'videographer', label: 'Videographer' },
    { value: 'editor_photos', label: 'Photo Editor' },
    { value: 'editor_video', label: 'Video Editor' },
    { value: 'assistant', label: 'Assistant' },
    { value: 'drone_operator', label: 'Drone Operator' },
    { value: 'voxpop_videographer', label: 'VoxPop Videographer' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(role)
        ? prev.skills.filter(r => r !== role)
        : [...prev.skills, role]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.skills.length === 0) {
      toast.error('Please select at least one role');
      return;
    }

    try {
      setCreating(true);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/create`,
        {
          ...formData,
          full_name: `${formData.first_name} ${formData.last_name}`.trim(),
          role: 'crew'
        }
      );

      if (response.data.success) {
        toast.success('User created successfully');
        onAdded();
        onClose();

        // Reset form
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          password: '',
          business_name: '',
          abn: '',
          gst_registered: false,
          account_status: 'active',
          approval_status: 'approved',
          skills: []
        });
      }
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const label = "text-xs font-semibold text-[#2B3142] mb-1.5 block";
  const select = "w-full h-[42px] px-3 rounded-[10px] border border-[#E0E3EB] text-[13px] bg-white outline-none focus:border-[#28C0D0]";

  return (
    <div className="sz-modal-backdrop sz-fade" onClick={onClose}>
      <div className="sz-modal-card w-full max-w-[600px]" onClick={(e) => e.stopPropagation()}>
        {/* Header (green = add) */}
        <div className="flex items-center justify-between px-6 py-[18px] text-white" style={{ background: GREEN }}>
          <div className="min-w-0">
            <h3 className="text-[17px] font-bold m-0" style={{ fontFamily: "Poppins" }}>Add New User</h3>
            <p className="text-white/80 text-xs mt-0.5">Manually create a user account</p>
          </div>
          <button onClick={onClose} className="w-[30px] h-[30px] rounded-[9px] bg-white/20 hover:bg-white/30 text-white flex items-center justify-center shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto szscroll px-6 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <Label className={label}>First Name *</Label>
              <Input value={formData.first_name} onChange={(e) => handleChange('first_name', e.target.value)} required />
            </div>
            <div>
              <Label className={label}>Last Name *</Label>
              <Input value={formData.last_name} onChange={(e) => handleChange('last_name', e.target.value)} required />
            </div>
            <div>
              <Label className={label}>Email *</Label>
              <Input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} required />
            </div>
            <div>
              <Label className={label}>Phone</Label>
              <Input value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} />
            </div>
            <div>
              <Label className={label}>Password *</Label>
              <Input type="password" value={formData.password} onChange={(e) => handleChange('password', e.target.value)} placeholder="Min. 6 characters" minLength={6} required />
            </div>
            <div>
              <Label className={label}>Business Name</Label>
              <Input value={formData.business_name} onChange={(e) => handleChange('business_name', e.target.value)} placeholder="Optional" />
            </div>
            <div>
              <Label className={label}>ABN</Label>
              <Input value={formData.abn} onChange={(e) => handleChange('abn', e.target.value)} placeholder="12 345 678 901" />
            </div>
            <div className="flex items-center gap-2 pt-7">
              <input type="checkbox" id="gst_new" checked={formData.gst_registered} onChange={(e) => handleChange('gst_registered', e.target.checked)} className="w-4 h-4 accent-[#572B7C]" />
              <Label htmlFor="gst_new" className="cursor-pointer text-sm">GST Registered</Label>
            </div>
            <div>
              <Label className={label}>Account Status</Label>
              <select value={formData.account_status} onChange={(e) => handleChange('account_status', e.target.value)} className={select}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div>
              <Label className={label}>Approval Status</Label>
              <select value={formData.approval_status} onChange={(e) => handleChange('approval_status', e.target.value)} className={select}>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Role chips */}
          <div className="mt-4.5">
            <div className="text-xs font-bold text-[#2B3142] mb-2.5" style={{ fontFamily: "Poppins" }}>Roles *</div>
            <div className="flex flex-wrap gap-2">
              {roleOptions.map((role) => {
                const on = formData.skills.includes(role.value);
                return (
                  <button
                    key={role.value}
                    type="button"
                    onClick={() => handleRoleToggle(role.value)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors"
                    style={on
                      ? { color: "#572B7C", background: "#EDE4F4", borderColor: "#D9C5E6" }
                      : { color: "#8A90A2", background: "#F5F6F9", borderColor: "#E6E9EF" }}
                  >
                    {role.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 mt-5 pt-4 border-t border-[#EEF0F4]">
            <button type="button" onClick={onClose} className="sz-btn sz-btn-outline">Cancel</button>
            <button type="submit" disabled={creating} className="sz-btn text-white" style={{ background: GREEN }}>
              {creating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating...</> : <><UserPlus className="w-4 h-4 mr-1.5" /> Create User</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAddUserModal;
